// Progression + daily-streak helpers (§5.2). Level is derived from the number
// of discovered elements (prototype: floor(discovered/6)+1). Streak persists the
// last-played date and increments on consecutive calendar days.
import { loadGameState, saveGameState } from "../services/storageService";

export const levelFromDiscovered = (discovered: number): number =>
  Math.max(1, Math.floor(discovered / 6) + 1);

/** Elements needed to reach the next level, and progress toward it (0..1). */
export const levelProgress = (discovered: number) => {
  const inLevel = discovered % 6;
  return { current: levelFromDiscovered(discovered), into: inLevel, size: 6, pct: inLevel / 6 };
};

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
const dayNumber = (d: Date) => Math.floor(d.getTime() / 86400000);

export interface StreakState {
  streak: number;
  lastDay: string | null;
  claimedToday: boolean;
}

// Streak data lives on the existing GameState (backward-compatible additions).

/** Read the current streak without mutating it. */
export const getStreak = (now = new Date()): StreakState => {
  const gs = loadGameState();
  return {
    streak: gs.streak ?? 0,
    lastDay: gs.streakLastDay ?? null,
    claimedToday: gs.streakClaimedDay === dayKey(now),
  };
};

/**
 * Register activity for "today". Increments the streak on the first play of a
 * new consecutive day, resets it if a day was skipped, and starts it at 1 the
 * first time. Returns the updated streak state. Idempotent within a day.
 */
export const touchStreak = (now = new Date()): StreakState => {
  const gs = loadGameState();
  const today = dayKey(now);
  const prevDay = gs.streakLastDay ?? null;
  let streak = gs.streak ?? 0;

  if (prevDay === today) {
    // already counted today
    streak = streak || 1;
  } else if (prevDay) {
    // gap of exactly 1 calendar day continues the streak; more resets it
    const prevParts = prevDay.split("-").map(Number);
    const prev = new Date(prevParts[0], prevParts[1] - 1, prevParts[2]);
    const gap = dayNumber(now) - dayNumber(prev);
    streak = gap === 1 ? streak + 1 : 1;
  } else {
    streak = 1;
  }

  saveGameState({ streak, streakLastDay: today });
  return { streak, lastDay: today, claimedToday: (gs.streakClaimedDay ?? null) === today };
};

/** Mark today's daily reward as claimed. Returns whether it was newly claimed. */
export const claimDaily = (now = new Date()): boolean => {
  const gs = loadGameState();
  const today = dayKey(now);
  if (gs.streakClaimedDay === today) return false;
  saveGameState({ streakClaimedDay: today });
  return true;
};
